/**
 * Phaser board sprites are 64×92 in game pixels. Sharp mode does not show
 * this bake: the scene cuts the face out of the 1024×768 canvas and the
 * page draws the full JPG plus the ATK/DEF. This size is the Estándar face,
 * the card back, and the cutout mask.
 */
export const BOARD_TEX_W = 128
export const BOARD_TEX_H = 184

/**
 * Add a downsampled canvas texture. No-op if `key` already exists.
 */
export function addPhaserCardTexture(
  scene: Phaser.Scene,
  key: string,
  img: HTMLImageElement,
): void {
  if (scene.textures.exists(key)) return
  const w = BOARD_TEX_W
  const h = BOARD_TEX_H
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    scene.textures.addImage(key, img)
    return
  }
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  drawCardTex(ctx, img, w, h)
  // A phone that dropped the bitmap still reports a decoded image. Drawing
  // it yields a black or empty canvas, and Phaser would keep that key.
  if (canvasIsBlank(ctx, w, h)) return
  scene.textures.addCanvas(key, canvas)
}

/**
 * True when the bake has no ink. Samples a grid so a dark sleeve with a
 * light mark still counts, and a solid black or transparent result does not.
 */
function canvasIsBlank(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
): boolean {
  let data: Uint8ClampedArray
  try {
    data = ctx.getImageData(0, 0, w, h).data
  } catch {
    return false
  }
  const step = 8
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      const i = (y * w + x) * 4
      if ((data[i + 3] ?? 0) < 8) continue
      if (
        (data[i] ?? 0) > 12 ||
        (data[i + 1] ?? 0) > 12 ||
        (data[i + 2] ?? 0) > 12
      ) {
        return false
      }
    }
  }
  return true
}

type DownsampleSource = CanvasImageSource & {
  naturalWidth?: number
  naturalHeight?: number
  width: number
  height: number
}

/**
 * Halve until the last step is about 2×. One jump from a 788 px sleeve
 * to the board size skips pixels and turns the line art into blocks.
 */
export function downsampleCard(
  ctx: CanvasRenderingContext2D,
  img: DownsampleSource,
  w: number,
  h: number,
): void {
  let src: CanvasImageSource = img
  let sw = img.naturalWidth || img.width
  let sh = img.naturalHeight || img.height
  while (sw > w * 2 && sh > h * 2) {
    const step = document.createElement('canvas')
    step.width = Math.max(w, Math.round(sw / 2))
    step.height = Math.max(h, Math.round(sh / 2))
    const stepCtx = step.getContext('2d')
    if (!stepCtx) break
    stepCtx.imageSmoothingEnabled = true
    stepCtx.imageSmoothingQuality = 'high'
    stepCtx.drawImage(src, 0, 0, step.width, step.height)
    src = step
    sw = step.width
    sh = step.height
  }
  ctx.drawImage(src, 0, 0, w, h)
}

function drawCardTex(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  w: number,
  h: number,
): void {
  downsampleCard(ctx, img, w, h)
}
